import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export type MarkDeliveredRequest = {
	completedTime: string;
};

export const postMarkDelivered = async (
	poNumber: string,
	body: MarkDeliveredRequest,
) => {
	const resp = await postAndValidate(
		apiRoutes.POSTMARKDELIVERED.replace(":poNumber", poNumber),
		body,
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostMarkDelivered = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		unknown,
		Error,
		{ poNumber: string } & MarkDeliveredRequest
	>({
		mutationKey: ["markDelivered"],
		mutationFn: ({ poNumber, ...body }) => postMarkDelivered(poNumber, body),
		// Awaiting the refetch keeps the mutation pending (and the spinner up)
		// until the shipment reflects the new state; toast once it's done.
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentMarkedDelivered);
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to mark shipment as delivered. Please try again.",
			);
		},
	});

	return {
		markDelivered: mutate,
		markDeliveredAsync: mutateAsync,
		isSavingMarkDelivered: isPending,
		error,
		data,
		resetError: reset,
	};
};
